import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  Tooltip,
  useTheme,
  Grid,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { RootState } from '../../types';
import { fetchClients, createClient, deleteClient, clearClientError } from '../../store/slices/clientsSlice';

const ClientList: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { clients, isLoading, error } = useSelector((state: RootState) => state.clients);
  const { user } = useSelector((state: RootState) => state.auth);
  
  // State for pagination and search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for create client dialog
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientDescription, setNewClientDescription] = useState('');
  
  // State for delete client dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<string | null>(null);
  
  // State for action menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  
  // Shared input styling using theme
  const inputFieldStyles = {
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      '&::placeholder': {
        color: theme.palette.text.disabled,
        opacity: 1,
      }
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.palette.border?.main || theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.border?.strong || theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.border?.focus || theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.Mui-focused': {
        color: theme.palette.border?.focus || theme.palette.primary.main,
      },
    },
  };
  
  // Fetch clients on component mount
  useEffect(() => {
    dispatch(fetchClients() as any);
  }, [dispatch]);
  
  // Check if user is admin
  const isAdmin = user?.role === 'global_admin';
  
  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    return clients.filter(client =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.id.toString().includes(searchTerm)
    );
  }, [clients, searchTerm]);

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search handler
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Menu handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, client: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClient(null);
  };
  
  // Handle create client dialog open
  const handleCreateDialogOpen = () => {
    setOpenCreateDialog(true);
  };
  
  // Handle create client dialog close
  const handleCreateDialogClose = () => {
    setOpenCreateDialog(false);
    setNewClientName('');
    setNewClientDescription('');
  };
  
  // Handle create client
  const handleCreateClient = () => {
    if (newClientName.trim()) {
      dispatch(createClient({
        name: newClientName.trim(),
        description: newClientDescription.trim() || undefined,
      }) as any).then((result: any) => {
        if (!result.error) {
          handleCreateDialogClose();
        }
      });
    }
  };
  
  // Handle delete client dialog open
  const handleDeleteDialogOpen = (clientId: string) => {
    setClientToDelete(clientId);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };
  
  // Handle delete client dialog close
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setClientToDelete(null);
  };
  
  // Handle delete client
  const handleDeleteClient = () => {
    if (clientToDelete) {
      dispatch(deleteClient(clientToDelete) as any).then((result: any) => {
        if (!result.error) {
          handleDeleteDialogClose();
        }
      });
    }
  };
  
  // Handle view client
  const handleViewClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
    handleMenuClose();
  };
  
  // Handle edit client
  const handleEditClient = (clientId: string) => {
    navigate(`/clients/${clientId}/edit`);
    handleMenuClose();
  };
  
  // Handle manage client access
  const handleManageAccess = (clientId: string) => {
    navigate(`/clients/${clientId}/access`);
    handleMenuClose();
  };
  
  // Handle refresh
  const handleRefresh = () => {
    dispatch(fetchClients() as any);
  };
  
  // Clear error
  const handleClearError = () => {
    dispatch(clearClientError());
  };
  
  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      py: 3,
    }}>
      <Box sx={{
        width: '90%',
        maxWidth: '90%',
        mx: 'auto',
      }}>
        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}>
          <Box>
            <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, marginBottom: 0.5 }}>
              Client List
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Manage and monitor your clients
            </Typography>
          </Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateDialogOpen}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                color: theme.palette.primary.contrastText,
                minWidth: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Add Client
            </Button>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            onClose={handleClearError} 
            sx={{ 
              mb: 3,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Search and Filter Section */}
        <Paper sx={{
          mb: 3,
          p: 3,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}>
          <Grid container spacing={3} alignItems="center">
            <Grid size = {{xs: 12, md: 8}}>
              <TextField
                fullWidth
                placeholder="Search clients by name, description, or ID..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  ...inputFieldStyles,
                  '& .MuiOutlinedInput-root': {
                    ...inputFieldStyles['& .MuiOutlinedInput-root'],
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid size = {{xs: 12, md: 4}}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                <IconButton
                  onClick={handleRefresh}
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.background.secondary,
                    border: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      transform: 'rotate(180deg)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Content Section */}
        {isLoading && clients.length === 0 ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 8,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress
                sx={{
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              />
              <Typography sx={{ color: theme.palette.text.secondary }}>
                Loading clients...
              </Typography>
            </Box>
          </Box>
        ) : filteredClients.length === 0 ? (
          <Paper sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            borderRadius: 2,
          }}>
            <Typography sx={{ color: theme.palette.text.primary, mb: 1 }}>
              No clients found
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary }}>
              {searchTerm ? 'Try adjusting your search terms' : 'No clients have been created yet'}
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}>
            <TableContainer sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.background.secondary,
                borderRadius: 1,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.border?.main || theme.palette.divider,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: theme.palette.border?.strong || theme.palette.primary.main,
                },
              },
            }}>
              <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                  }}>
                    {[
                      'Client Name', 'Description', 'Tickets', 'Created', 'Actions'
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          borderBottom: `2px solid ${theme.palette.border?.main || theme.palette.divider}`,
                          backgroundColor: 'transparent',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((client) => (
                      <TableRow
                        key={client.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: theme.palette.interactive?.hover || theme.palette.action.hover,
                            transform: 'translateX(4px)',
                          },
                          borderBottom: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
                          '&:last-child': {
                            borderBottom: 'none',
                          }
                        }}
                      >
                        <TableCell sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 500,
                          borderBottom: 'none',
                          minWidth: 200,
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {client.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            ID: {client.id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{
                          color: theme.palette.text.primary,
                          borderBottom: 'none',
                          maxWidth: 300,
                        }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {client.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none', minWidth: 100 }}>
                          <Chip
                            label={client.tickets_count || 0}
                            size="small"
                            sx={{
                              bgcolor: `${theme.palette.info.main}20`,
                              color: theme.palette.info.main,
                              fontWeight: 600,
                              borderRadius: 2,
                              border: `1px solid ${theme.palette.info.main}40`
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{
                          color: theme.palette.text.secondary,
                          borderBottom: 'none',
                          fontSize: '0.85rem',
                          whiteSpace: 'nowrap',
                        }}>
                          {new Date(client.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none', minWidth: 120 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewClient(client.id);
                                }}
                                sx={{ 
                                  color: theme.palette.text.secondary,
                                  '&:hover': { color: theme.palette.primary.main }
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isAdmin && (
                              <>
                                <Tooltip title="Edit Client">
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditClient(client.id);
                                    }}
                                    sx={{ 
                                      color: theme.palette.text.secondary,
                                      '&:hover': { color: theme.palette.primary.main }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="More Actions">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuClick(e, client);
                                    }}
                                    sx={{ 
                                      color: theme.palette.text.secondary,
                                      '&:hover': { color: theme.palette.primary.main }
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredClients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  color: theme.palette.text.primary,
                  '& .MuiTablePagination-toolbar': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiTablePagination-select': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiTablePagination-selectIcon': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiTablePagination-actions .MuiIconButton-root': {
                    color: theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: theme.palette.interactive?.hover || theme.palette.action.hover,
                    },
                    '&.Mui-disabled': {
                      color: theme.palette.text.disabled,
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            }
          }}
        >
          <MenuItem onClick={() => handleViewClient(selectedClient?.id)} sx={{ gap: 1 }}>
            <ViewIcon fontSize="small" />
            View Details
          </MenuItem>
          <MenuItem onClick={() => handleEditClient(selectedClient?.id)} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" />
            Edit Client
          </MenuItem>
          <MenuItem onClick={() => handleManageAccess(selectedClient?.id)} sx={{ gap: 1 }}>
            <PeopleIcon fontSize="small" />
            Manage Access
          </MenuItem>
          <MenuItem 
            onClick={() => handleDeleteDialogOpen(selectedClient?.id)} 
            sx={{ 
              gap: 1, 
              color: theme.palette.error.main 
            }}
          >
            <DeleteIcon fontSize="small" />
            Delete Client
          </MenuItem>
        </Menu>
        
        {/* Create Client Dialog */}
        <Dialog 
          open={openCreateDialog} 
          onClose={handleCreateDialogClose}
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.border?.main || theme.palette.divider}`,
              borderRadius: 2,
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          <DialogTitle sx={{
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            fontWeight: 700,
          }}>
            Create New Client
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Client Name"
              type="text"
              fullWidth
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              required
              sx={inputFieldStyles}
            />
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={newClientDescription}
              onChange={(e) => setNewClientDescription(e.target.value)}
              sx={{ mt: 2, ...inputFieldStyles }}
            />
          </DialogContent>
          <DialogActions sx={{
            borderTop: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            p: 2,
          }}>
            <Button
              onClick={handleCreateDialogClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.interactive?.hover || theme.palette.action.hover,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateClient}
              variant="contained"
              disabled={!newClientName.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Delete Client Dialog */}
        <Dialog 
          open={openDeleteDialog} 
          onClose={handleDeleteDialogClose}
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.border?.main || theme.palette.divider}`,
              borderRadius: 2,
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          <DialogTitle sx={{
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            fontWeight: 700,
          }}>
            Delete Client
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <DialogContentText sx={{ color: theme.palette.text.secondary }}>
              Are you sure you want to delete this client? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{
            borderTop: `1px solid ${theme.palette.border?.light || theme.palette.divider}`,
            p: 2,
          }}>
            <Button
              onClick={handleDeleteDialogClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.interactive?.hover || theme.palette.action.hover,
                },
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteClient} 
              variant="contained"
              sx={{
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ClientList;